// « Qui agit ? » — la porte commune à toutes les actions qui bougent de
// l'argent ou du stock. Le COMPTE reste connecté sur l'écran ; la PERSONNE se
// fait reconnaître (code tapé au clavier) au moment d'agir.
//
//   const who = await requestIdentity();                       // n'importe qui
//   const who = await requestIdentity({ minLevel: 'manager' }); // action réservée
//   if (who === false) return;          // annulé -> on ne fait rien
//   await withIdentity(who, () => service.doTheThing());        // qui l'a fait
//
//   who = { id, name, level, method, token } | null (rien à demander : aucune
//   personne n'a encore de code) | false (annulé)
//
// La fenêtre du code est IdentityGateHost. withIdentity() pose l'en-tête
// X-Kipara-Actor sur les requêtes de l'action : le serveur y lit QUI a agi
// (journal) et, sur une caisse, si c'est un manager — alors sans limite.
import axios from 'axios';
import authService, { apiClient } from './authService';
import sellerIdentityService from './sellerIdentityService';
import { getManagerSession } from './managerSession';

export const ACTOR_HEADER = 'X-Kipara-Actor';

let host = null;       // set by <IdentityGateHost/> (App root)
let busy = false;      // one popup at a time (double clicks)
let current = null;    // identity stamped on outgoing requests

export function registerIdentityHost(open) {
  host = open;
  return () => { if (host === open) host = null; };
}

export async function requestIdentity(options = {}) {
  if (busy) return false;
  busy = true;
  try {
    // Caisse en mode manager : c'est le manager reconnu qui agit (sans limite).
    const session = getManagerSession();
    if (session && !options.always) return session.who;
    if (!options.minLevel && !options.always && authService.isAdmin()) return null;
    let status = null;
    try { status = await sellerIdentityService.getStatus(); } catch (_) { /* injoignable : l'action dira l'erreur */ }
    // Une action réservée demande toujours quelqu'un. Sinon on ne demande
    // rien tant qu'aucune personne n'est configurée, ni sur un compte
    // propriétaire / manager : ce compte-là dit déjà qui agit (owner
    // 2026-09-21) — c'est le compte VENDEUR, partagé, qui a besoin du code.
    if (!options.minLevel && (!status || !status.required)) return null;
    if (!host) return options.minLevel ? false : null;
    return await new Promise((resolve) => host({ options, resolve }));
  } finally {
    busy = false;
  }
}

export const actorHeaderValue = (who) => (who && who.id ? `${who.id}:${who.method || 'pin'}:${who.token || ''}` : '');

// Same information for a request BODY (the sale keeps it there so an offline
// replay, which only stores the body, still knows who sold).
export const actorFields = (who) => (who && who.id
  ? { actor: who.id, actor_token: who.token || '', actor_method: who.method || 'pin' }
  : {});

export async function withIdentity(who, fn) {
  const previous = current;
  current = who || null;
  try {
    return await fn();
  } finally {
    current = previous;
  }
}

// Same as withIdentity for code that already has its own try / finally:
//   const done = stampAs(who); try { … } finally { done(); }
export function stampAs(who) {
  const previous = current;
  current = who || null;
  return () => { current = previous; };
}

// One-liner for a single call:
//   if ((await gated('Qui bloque ?', () => service.block(id))) === GATE_CANCELLED) return;
export const GATE_CANCELLED = Symbol('identity-gate-cancelled');
export async function gated(title, fn, options = {}) {
  const who = await requestIdentity({ title, ...options });
  if (who === false) return GATE_CANCELLED;
  return withIdentity(who, () => fn(who));
}

// Ask, then run: the usual two lines in one. Resolves to undefined when the
// person cancels (nothing was done).
export async function asPerson(options, fn) {
  const who = await requestIdentity(options);
  if (who === false) return undefined;
  return withIdentity(who, () => fn(who));
}

export function stampActor(config) {
  // L'action en cours d'abord ; sinon, en mode manager, le manager reconnu :
  // toutes les requêtes portent sa preuve (pages réservées ouvertes).
  const session = getManagerSession();
  const who = (current && current.id) ? current : (session ? session.who : null);
  if (who && who.id) {
    config.headers = config.headers || {};
    config.headers[ACTOR_HEADER] = actorHeaderValue(who);
  }
  return config;
}

// The shared apiClient, and the global axios that a few services (réservations,
// soins, catégories clients) still call directly.
apiClient.interceptors.request.use(stampActor);
axios.interceptors.request.use(stampActor);
